import { makeStyles } from 'tss-react/mui';
import Colors from '../../../utils/color';
import React from 'react';

/**
 * Only the OPTION LIST columnises. Groups never do — a group row is a full-width
 * line at every pane width, which is the whole point of the row rhythm.
 *
 * Container queries would be the honest tool, but the pane is resized by
 * `react-resizable-panels` rather than by the viewport, so `auto-fill` against a
 * minimum column width gets the same result without measuring: 2 columns once
 * there is room for two ~190px tracks, 3 once there is room for three.
 */
const MIN_COLUMN_PX = 186;

const useStyles = makeStyles()(() => ({
  // No fill. Every drawer is open by default, so a tinted drawer would tint
  // essentially the whole pane — and it would compete with the ONE background
  // that has to mean something here: `Colors.alto` on the group the agent is
  // currently on, which `ModGroup.modGroupClass` paints and the Agent Cart uses
  // for exactly the same purpose (`CartItemModGroups.selectedModGroupClass`).
  // The gap between a group's options and the NEXT group's row. This is the
  // separation that makes the groups read as groups, not spare space.
  drawer: {
    display: 'flex',
    marginBottom: 4,
  },
  // The left rail. With the columnised list it already reads as a picker,
  // which is why the frozen design carries no verb chip: an unmet constraint is
  // said once, by the group row's own `! Choose N`.
  //
  // A neutral hairline, not a blue bar. With no drawer fill this is the only
  // thing tying a group's options to the row above them, so it stays.
  //
  // `silverChalice`, NOT `alto`: the selected group is painted `alto` by
  // `ModGroup.modGroupClass`, so an `alto` rail disappeared into the one group
  // the agent was actually working in. The rail has to read against white AND
  // against the selection highlight.
  rail: {
    flexShrink: 0,
    width: 2,
    backgroundColor: Colors.silverChalice,
  },
  options: {
    flex: 1,
    minWidth: 0,
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_COLUMN_PX}px, 1fr))`,
    // Columns only. Each row carries a disclosure marker on its left edge and,
    // when the option has a spoken description, an info button on its right —
    // so with the tracks flush, one row's info button sat hard against the next
    // column's marker. Two different controls, a pixel apart, and a misclick
    // speaks to the customer. Rows stay flush: they are 24px tall and there is
    // nothing at their edges to collide.
    columnGap: 8,
    // Vertical only, and half what it was. Horizontally the grid is flush with
    // the rail: the fixed-width marker cell already holds every name clear of
    // it, so a horizontal inset here was pure lost width, paid once per group
    // down the whole pane.
    padding: '2px 0',
  },
}));

interface OptionDrawerProps {
  children: React.ReactNode;
  /** Indent depth of the group this drawer belongs to. */
  indentPx?: number;
}

/**
 * HITL-1616 — a group's own options, so a picker can never be misread as child
 * rows.
 */
const OptionDrawer = ({ children, indentPx = 0 }: OptionDrawerProps) => {
  const { classes } = useStyles();
  return (
    <div
      className={classes.drawer}
      style={indentPx ? { marginLeft: indentPx } : undefined}
      data-testid="option-drawer"
    >
      <div className={classes.rail} />
      <div className={classes.options}>{children}</div>
    </div>
  );
};

export default OptionDrawer;
